import React from "react";
import { Link } from "react-router-dom";
import {
  Layers,
  FileSignature,
  Shield,
  Database,
  Sparkles,
  EyeOff,
  ScrollText,
  Stamp,
  Network,
  ArrowRight,
  Minus,
  Plus,
} from "lucide-react";

const STEPS = [
  {
    n: "01",
    title: "Fragmented estate",
    Icon: Layers,
    body:
      "Data, systems, policies, controls and responsibilities sit across different legacy environments. A catalogue may describe what exists, but it does not by itself govern what may happen next.",
  },
  {
    n: "02",
    title: "Sponsor request",
    Icon: ScrollText,
    body:
      "A transformation sponsor requests reuse of a dataset for an AI-assisted impact analysis. The request declares purpose, intended use, policy context and expected downstream action.",
  },
  {
    n: "03",
    title: "Data remains at source",
    Icon: Database,
    body:
      "The dataset is not moved into a new central database. GovDataCierge surfaces metadata, evidence references, custody owner and control references only.",
  },
  {
    n: "04",
    title: "Authority check",
    Icon: Shield,
    body:
      "The data steward, policy owner and assurance reviewer each see the request through their role. They confirm whether the purpose, lawful basis, control boundary and evidence are sufficient.",
  },
  {
    n: "05",
    title: "AI advisory boundary",
    Icon: Sparkles,
    body:
      "A deterministic advisory note flags dependencies, missing evidence and possible downstream risks. AI may suggest. Authorised reviewers decide. KATLAS signs the receipt.",
  },
  {
    n: "06",
    title: "Evidence boundary",
    Icon: EyeOff,
    body:
      "Shared evidence references are recorded. Withheld evidence is counted but not exposed. Sensitive material stays in local custody.",
  },
  {
    n: "07",
    title: "Governed decision",
    Icon: Stamp,
    body:
      "The request is approved, refused or escalated. The decision is role-bound and purpose-bound.",
  },
  {
    n: "08",
    title: "KATLAS receipt",
    Icon: FileSignature,
    body:
      "KATLAS records the governed action: who requested, who reviewed, what purpose was declared, what evidence was shared or withheld, and what outcome followed.",
  },
  {
    n: "09",
    title: "Federated operating view",
    Icon: Network,
    body:
      "The sponsor sees the operational picture without centralising raw data or creating a single administrative point of failure.",
  },
];

const CENTRALISED = [
  "copies data into a new master store",
  "increases dependency on one platform",
  "creates administration and access bottlenecks",
  "can become another single point of failure",
  "may obscure local custody and accountability",
];

const FEDERATED = [
  "data remains at source",
  "authority is checked at point of action",
  "evidence references travel, not raw data",
  "approvals and refusals are attributable",
  "receipts create the shared assurance layer",
];

const DESIGN_IDEAS = [
  "AI-ready data reuse should be governed at the point of action, not only catalogued in advance.",
  "A holistic view can be federated rather than centralised.",
  "Metadata, lineage and graph tools need an operational governance layer around reuse, transformation and AI-assisted action.",
  "AI-generated advisory notes should remain subject to human authority and evidence checks.",
  "Assurance should be designed into the workflow, not assembled retrospectively.",
  "Public-sector transformation can preserve local custody while producing enterprise-level evidence.",
];

export default function Walkthrough() {
  return (
    <main data-testid="walkthrough-page">
      {/* Page header */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-12">
        <p className="eyebrow">Design walkthrough</p>
        <h1
          className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight font-light text-[var(--ink-heading)] mt-4 max-w-4xl"
          data-testid="walkthrough-title"
        >
          How GovDataCierge creates a federated operating view.
        </h1>
        <p className="text-lg text-[var(--ink-secondary)] mt-8 max-w-3xl leading-relaxed">
          GovDataCierge is a synthetic sandbox showing how a public-sector
          sponsor can govern AI-ready data reuse across fragmented systems. It
          does not replace catalogue, graph or metadata tools. It shows how
          requests, purpose, authority, evidence boundaries and receipts can be
          coordinated while sensitive data remains at source.
        </p>
      </section>

      {/* Steps */}
      <section className="bg-[var(--bg-secondary)] border-y border-slate-200 bg-grain">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
            <div className="lg:col-span-4">
              <p className="eyebrow">Nine steps</p>
              <h2 className="font-display text-3xl md:text-4xl text-[var(--ink-heading)] mt-4 leading-snug">
                From fragmented estate to federated operating view.
              </h2>
            </div>
            <div className="lg:col-span-8 text-[var(--ink-secondary)] text-lg leading-relaxed">
              Each step shows where the boundary holds. Data does not leave
              local custody. AI may suggest, but every decision is bound to a
              role, a purpose and a policy — and every governed action ends in
              a KATLAS receipt.
            </div>
          </div>

          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="walkthrough-steps">
            {STEPS.map((s) => {
              const Icon = s.Icon;
              return (
                <li
                  key={s.n}
                  className="border border-slate-200 bg-white p-7 flex flex-col gap-4"
                  data-testid={`walkthrough-step-${s.n}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono-ui text-xs tracking-[0.22em] text-[var(--ink-mono)]">
                      {s.n}
                    </span>
                    <Icon size={18} className="text-[var(--ink-heading)]" />
                  </div>
                  <h3 className="font-display text-xl text-[var(--ink-heading)] leading-snug">
                    {s.title}
                  </h3>
                  <p className="text-sm text-[var(--ink-secondary)] leading-relaxed">
                    {s.body}
                  </p>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* Comparison panel */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="comparison-panel">
        <div className="mb-10">
          <p className="eyebrow">Architecture comparison</p>
          <h2 className="font-display text-3xl md:text-4xl text-[var(--ink-heading)] mt-3 leading-snug max-w-3xl">
            Centralised view vs federated operating view.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-slate-200 bg-white p-8" data-testid="centralised-column">
            <p className="eyebrow text-[var(--ink-muted)]">Centralised view</p>
            <h3 className="font-display text-2xl text-[var(--ink-heading)] mt-2 mb-5">
              A single database.
            </h3>
            <ul className="space-y-3">
              {CENTRALISED.map((item) => (
                <li key={item} className="flex gap-3 text-[var(--ink-secondary)] text-sm leading-relaxed">
                  <Minus size={16} className="shrink-0 mt-1 text-[var(--ink-muted)]" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="border border-[var(--ink-heading)] bg-[var(--ink-heading)] text-[var(--bg-primary)] p-8" data-testid="federated-column">
            <p className="eyebrow text-slate-400">Federated operating view</p>
            <h3 className="font-display text-2xl mt-2 mb-5">
              A shared view, not a shared store.
            </h3>
            <ul className="space-y-3">
              {FEDERATED.map((item) => (
                <li key={item} className="flex gap-3 text-slate-200 text-sm leading-relaxed">
                  <Plus size={16} className="shrink-0 mt-1 text-slate-300" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* What this informs */}
      <section className="bg-[var(--bg-secondary)] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="design-ideas-section">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="eyebrow">For advisory audiences</p>
              <h2 className="font-display text-3xl md:text-4xl text-[var(--ink-heading)] mt-3 leading-snug">
                Design ideas this sandbox is intended to inform.
              </h2>
            </div>
            <ol className="lg:col-span-7 space-y-5">
              {DESIGN_IDEAS.map((idea, idx) => (
                <li
                  key={idx}
                  className="grid grid-cols-[auto_1fr] gap-5 items-start border-t border-slate-200 pt-5 first:border-t-0 first:pt-0"
                  data-testid={`design-idea-${idx + 1}`}
                >
                  <span className="font-mono-ui text-xs tracking-[0.22em] text-[var(--ink-mono)] mt-1">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <p className="text-[var(--ink-secondary)] leading-relaxed text-base md:text-lg">
                    {idea}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Disclaimer + CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7 border border-slate-200 bg-white p-8" data-testid="disclaimer-panel">
            <p className="eyebrow">Non-procurement disclaimer</p>
            <p className="text-[var(--ink-secondary)] mt-3 leading-relaxed">
              This sandbox is not an HMRC system, not a data catalogue
              replacement and not a procurement claim. It is a synthetic design
              demonstrator showing how KATLAS-style operational governance
              could complement metadata, catalogue, graph and AI tooling in
              complex public-sector estates.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-4">
            <p className="eyebrow">Follow the walkthrough end-to-end</p>
            <p className="text-[var(--ink-secondary)] leading-relaxed">
              The sponsor view shows the same nine-step pattern applied to five
              synthetic reuse requests. Open one to see custody, authority,
              evidence boundary and the KATLAS receipt.
            </p>
            <div className="flex flex-wrap gap-4 mt-2">
              <Link
                to="/sponsor"
                className="inline-flex items-center gap-2 bg-[var(--ink-heading)] text-[var(--bg-primary)] px-5 py-3 text-sm font-medium tracking-wide hover:bg-black transition-colors"
                data-testid="walkthrough-cta-sponsor"
              >
                Open sponsor view <ArrowRight size={16} />
              </Link>
              <Link
                to="/requests"
                className="inline-flex items-center gap-2 text-sm text-[var(--ink-heading)] underline underline-offset-4 decoration-slate-300 hover:decoration-slate-900"
                data-testid="walkthrough-cta-requests"
              >
                Follow a data reuse request
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
